import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { PaginationBar } from '@/components/ui/PaginationBar';
import { RobotSiteHierarchyChips } from '@/components/RobotSiteHierarchyChips';
import { buildRobotSiteDisplayRows, type RobotSiteRow } from '@/lib/robotSiteTree';
import {
  ForwardCycleTaskModal,
  type ForwardCycleTaskRow,
} from '@/components/ForwardCycleTaskModal';
import {
  inlineErrorClass,
  inlineSuccessClass,
  inputClass,
  tableClass,
  tableWrapClass,
  thClass,
  theadClass,
} from '@/components/ui/inputStyles';
import { cn } from '@/lib/cn';

type CompletedRobotChip = { id: number; name: string };

type TaskRow = {
  id: number;
  site_id?: number;
  entry_id: number;
  type_key?: string;
  type_label?: string;
  completed_robot_ids: number[];
  completed_robots?: CompletedRobotChip[];
  completed_groups_robot_ids?: number[];
  completed_groups_robots?: CompletedRobotChip[];
  completed_moments_robot_ids?: number[];
  completed_moments_robots?: CompletedRobotChip[];
  title_text: string;
  body_text: string;
  serial_number: string;
  created_at: string;
  /** 小程序 # 短链；与公众号跟圈发圈无关 */
  miniprogram_hash_link?: string;
};

type ListTasksResp = {
  ok: boolean;
  items: TaskRow[];
  total: number;
  page: number;
  per_page: number;
  q?: string;
  includes_child_sites?: boolean;
  scope_site_ids?: number[];
};

type PickerEntry = {
  id: number;
  serial_number: string | null;
  title_text: string | null;
  body_text: string | null;
  created_at: string;
  type_key?: string;
  type_label?: string;
};

type PickerResp = { ok: boolean; items: PickerEntry[]; total: number; page: number; per_page: number };

type MpBriefResp = {
  ok: boolean;
  miniprogram?: {
    id: number;
    name: string;
    site_ids: number[];
    entry_site_options?: Array<{ id: number; site_name: string }>;
  };
};

type RobotSummary = {
  site_id: number;
  robot_module_enabled?: boolean;
  forward_task_count: number;
  forward_task_count_aggregate?: number;
};

function forwardTaskErrMessage(err?: string, detail?: string): string {
  if (err === 'robot_module_disabled') {
    return (
      detail ||
      '本站点在本小程序下未开启「转发模块」。请返回小程序机器人管理页，对该站点点击「开启转发」后再加入队列。'
    );
  }
  return detail?.trim() || err || '加入失败';
}

function entryTypeBadge(label?: string, key?: string) {
  const text = (label || key || '').trim();
  if (!text) {
    return <span className="text-slate-400">—</span>;
  }
  return (
    <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-700 ring-1 ring-slate-200/90">
      {text}
    </span>
  );
}

function ProgressBar({ done, total, tone = 'teal' }: { done: number; total: number; tone?: 'teal' | 'sky' | 'emerald' }) {
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  const fill =
    tone === 'sky'
      ? 'bg-sky-500'
      : tone === 'emerald'
        ? 'bg-emerald-500'
        : 'bg-teal-500';
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
      <div className={cn('h-full rounded-full transition-all duration-500', fill)} style={{ width: `${pct}%` }} />
    </div>
  );
}

function channelLabel(channel: string): string {
  if (channel === 'moments') return '仅发圈';
  if (channel === 'groups') return '仅群发';
  return '发圈 + 群发';
}

function HashLinkCell({
  hash,
  onCopy,
  onRegen,
}: {
  hash: string;
  onCopy: () => void;
  onRegen?: () => void;
}) {
  if (!hash) {
    return (
      <div className="space-y-1.5">
        <span className="inline-flex items-center gap-1 rounded-lg bg-amber-50 px-2 py-1 text-xs text-amber-900 ring-1 ring-amber-200/80">
          <i className="fa-solid fa-triangle-exclamation text-[10px]" aria-hidden />
          未生成
        </span>
        {onRegen ? (
          <Button type="button" variant="secondary" size="sm" onClick={onRegen}>
            补生成短链
          </Button>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <div
        className="line-clamp-2 break-all rounded-lg bg-slate-50/90 px-2.5 py-2 font-mono text-[11px] leading-snug text-slate-700 ring-1 ring-slate-200/70"
        title={hash}
      >
        {hash}
      </div>
      <Button type="button" variant="ghost" size="sm" className="!px-2 !text-teal-700 hover:!bg-teal-50" onClick={onCopy}>
        <i className="fa-regular fa-copy mr-1 text-[11px]" aria-hidden />
        复制
      </Button>
    </div>
  );
}

export function RobotForwardTasksPage() {
  const { mpId: mpIdParam, siteId: siteIdParam } = useParams();
  const mpId = Math.max(0, parseInt(mpIdParam || '0', 10) || 0);
  const siteId = Math.max(0, parseInt(siteIdParam || '0', 10) || 0);
  const [mpName, setMpName] = useState('');
  const [sites, setSites] = useState<RobotSiteRow[]>([]);
  const [queueDirectById, setQueueDirectById] = useState<Record<number, number>>({});
  const [queueAggregateById, setQueueAggregateById] = useState<Record<number, number>>({});
  const [robotModuleBySiteId, setRobotModuleBySiteId] = useState<Record<number, boolean>>({});
  const [includesChildSites, setIncludesChildSites] = useState(false);
  const [siteName, setSiteName] = useState('');
  const [data, setData] = useState<TaskRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [notice, setNotice] = useState('');
  const perPage = 15;
  /** 已生效的列表筛选关键字（登记编号精确匹配或标题/正文/编号模糊） */
  const [listQ, setListQ] = useState('');
  const [listQDraft, setListQDraft] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [picker, setPicker] = useState<PickerEntry[]>([]);
  const [pickerTotal, setPickerTotal] = useState(0);
  const [pickerPage, setPickerPage] = useState(1);
  const [pickerQ, setPickerQ] = useState('');
  const [pickerLoading, setPickerLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [removingRobotKey, setRemovingRobotKey] = useState<string | null>(null);

  const [cycleOpen, setCycleOpen] = useState(false);
  const [cycleEdit, setCycleEdit] = useState<ForwardCycleTaskRow | null>(null);
  const [cycleItems, setCycleItems] = useState<ForwardCycleTaskRow[]>([]);
  const [cycleTotal, setCycleTotal] = useState(0);
  const [cycleLoading, setCycleLoading] = useState(false);
  const [cycleBusyId, setCycleBusyId] = useState<number | null>(null);
  const [cycleStatusFilter, setCycleStatusFilter] = useState<'all' | 'active' | 'paused' | 'completed'>('all');

  const loadMpAndSites = useCallback(async () => {
    if (mpId < 1) {
      setMpName('');
      setSites([]);
      return;
    }
    const mp = await apiFetch<MpBriefResp>('get_miniprogram', { method: 'GET', query: { id: mpId } });
    if (!mp.ok || !mp.miniprogram) {
      setMpName('');
      setSites([]);
      return;
    }
    setMpName(mp.miniprogram.name);
    const opts = Array.isArray(mp.miniprogram.entry_site_options) ? mp.miniprogram.entry_site_options : [];
    const scopeIds = new Set<number>(
      opts.length > 0
        ? opts.map((o) => Number(o.id)).filter((x) => x > 0)
        : (mp.miniprogram.site_ids || []).map((x) => Number(x)).filter((x) => x > 0),
    );
    const ls = await apiFetch<{ ok: boolean; items: RobotSiteRow[] }>('list_sites', {
      method: 'GET',
      query: { page: 1, per_page: 500, only_roots: 0 },
    });
    let filtered: RobotSiteRow[] = [];
    if (ls.ok) {
      filtered = ls.items.filter((s) => scopeIds.has(s.id));
      const have = new Set(filtered.map((s) => s.id));
      for (const o of opts) {
        const oid = Number(o.id);
        if (oid > 0 && !have.has(oid)) {
          filtered.push({
            id: oid,
            site_code: '',
            site_name: String(o.site_name ?? `站点 #${oid}`),
            parent_site_id: null,
          });
        }
      }
    } else if (opts.length > 0) {
      filtered = opts.map((o) => ({
        id: Number(o.id),
        site_code: '',
        site_name: String(o.site_name ?? `站点 #${o.id}`),
        parent_site_id: null,
      }));
    }
    setSites(filtered);

    const ids = filtered.map((s) => s.id);
    if (ids.length === 0) {
      setQueueDirectById({});
      setQueueAggregateById({});
      setRobotModuleBySiteId({});
      return;
    }
    const b = await apiFetch<{ ok: boolean; items: RobotSummary[] }>('batch_robot_site_summary', {
      method: 'POST',
      body: JSON.stringify({ site_ids: ids, miniprogram_id: mpId }),
    });
    if (b.ok) {
      const direct: Record<number, number> = {};
      const agg: Record<number, number> = {};
      const moduleOn: Record<number, boolean> = {};
      for (const it of b.items) {
        direct[it.site_id] = it.forward_task_count ?? 0;
        agg[it.site_id] = it.forward_task_count_aggregate ?? it.forward_task_count ?? 0;
        moduleOn[it.site_id] = it.robot_module_enabled === true;
      }
      setQueueDirectById(direct);
      setQueueAggregateById(agg);
      setRobotModuleBySiteId(moduleOn);
    }
  }, [mpId]);

  const siteDisplayRows = useMemo(() => buildRobotSiteDisplayRows(sites), [sites]);

  const siteNameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const s of sites) m.set(s.id, s.site_name);
    return m;
  }, [sites]);

  const loadSiteName = useCallback(() => {
    if (siteId < 1) {
      setSiteName('');
      return;
    }
    const hit = sites.find((x) => x.id === siteId);
    setSiteName(hit?.site_name ?? '');
  }, [siteId, sites]);

  const loadTasks = useCallback(async () => {
    if (siteId < 1 || mpId < 1) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setMsg('');
    setNotice('');
    try {
      const r = await apiFetch<ListTasksResp>('list_forward_tasks', {
        method: 'GET',
        query: {
          site_id: siteId,
          miniprogram_id: mpId,
          page,
          per_page: perPage,
          ...(listQ.trim() ? { q: listQ.trim() } : {}),
        },
      });
      if (r.ok) {
        setData(r.items);
        setTotal(r.total);
        setIncludesChildSites(r.includes_child_sites === true);
      } else {
        setMsg('加载失败');
      }
    } finally {
      setLoading(false);
    }
  }, [siteId, mpId, page, listQ]);

  const loadCycleTasks = useCallback(async () => {
    if (siteId < 1 || mpId < 1) {
      setCycleItems([]);
      setCycleTotal(0);
      return;
    }
    setCycleLoading(true);
    try {
      const r = await apiFetch<{ ok: boolean; items: ForwardCycleTaskRow[]; total: number }>(
        'list_forward_cycle_tasks',
        {
          method: 'GET',
          query: {
            site_id: siteId,
            miniprogram_id: mpId,
            page: 1,
            per_page: 50,
            ...(cycleStatusFilter !== 'all' ? { status: cycleStatusFilter } : {}),
          },
        },
      );
      if (r.ok) {
        setCycleItems(r.items || []);
        setCycleTotal(r.total || 0);
      }
    } finally {
      setCycleLoading(false);
    }
  }, [siteId, mpId, cycleStatusFilter]);

  function runListSearch() {
    setListQ(listQDraft.trim());
    setPage(1);
  }

  function resetListSearch() {
    setListQDraft('');
    setListQ('');
    setPage(1);
  }

  const loadPicker = useCallback(async () => {
    if (siteId < 1 || !addOpen) return;
    setPickerLoading(true);
    try {
      const r = await apiFetch<PickerResp>('list_entries_robot_picker', {
        method: 'GET',
        query: {
          site_id: siteId,
          miniprogram_id: mpId,
          page: pickerPage,
          per_page: 12,
          ...(pickerQ.trim() ? { q: pickerQ.trim() } : {}),
        },
      });
      if (r.ok) {
        setPicker(r.items);
        setPickerTotal(r.total);
      }
    } finally {
      setPickerLoading(false);
    }
  }, [siteId, addOpen, pickerPage, pickerQ]);

  useEffect(() => {
    void loadMpAndSites();
  }, [loadMpAndSites]);

  useEffect(() => {
    loadSiteName();
  }, [loadSiteName]);

  useEffect(() => {
    setPage(1);
  }, [siteId]);

  useEffect(() => {
    void loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    void loadCycleTasks();
  }, [loadCycleTasks]);

  useEffect(() => {
    void loadPicker();
  }, [loadPicker]);

  async function addTask(entryId: number) {
    setMsg('');
    const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>('add_forward_task', {
      method: 'POST',
      body: JSON.stringify({ site_id: siteId, entry_id: entryId, miniprogram_id: mpId }),
    });
    if (!r.ok) {
      setMsg(forwardTaskErrMessage(r.err, r.detail));
      return;
    }
    setAddOpen(false);
    await loadTasks();
  }

  async function copyHashLink(link: string) {
    setNotice('');
    setMsg('');
    try {
      await navigator.clipboard.writeText(link);
      setNotice('已复制小程序链接到剪贴板');
    } catch {
      setMsg('复制失败，请手动选择链接文字复制');
    }
  }

  async function setCycleStatus(row: ForwardCycleTaskRow, status: 'active' | 'paused') {
    setCycleBusyId(row.id);
    setMsg('');
    setNotice('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>('update_forward_cycle_task', {
        method: 'POST',
        body: JSON.stringify({
          id: row.id,
          site_id: siteId,
          miniprogram_id: mpId,
          status,
        }),
      });
      if (!r.ok) {
        setMsg(r.detail || r.err || '更新失败');
        return;
      }
      setNotice(status === 'paused' ? '已暂停循环任务' : '已恢复循环任务');
      await loadCycleTasks();
    } finally {
      setCycleBusyId(null);
    }
  }

  async function deleteCycleTask(row: ForwardCycleTaskRow) {
    if (!window.confirm(`确定删除循环任务「${row.title || `#${row.id}`}」？删除后进度记录也会清除。`)) {
      return;
    }
    setCycleBusyId(row.id);
    setMsg('');
    setNotice('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>('delete_forward_cycle_task', {
        method: 'POST',
        body: JSON.stringify({
          id: row.id,
          site_id: siteId,
          miniprogram_id: mpId,
        }),
      });
      if (!r.ok) {
        setMsg(r.detail || r.err || '删除失败');
        return;
      }
      setNotice('已删除循环任务');
      await loadCycleTasks();
    } finally {
      setCycleBusyId(null);
    }
  }

  function cycleStatusBadge(status: string) {
    if (status === 'active') {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-800 ring-1 ring-emerald-200/90">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" aria-hidden />
          进行中
        </span>
      );
    }
    if (status === 'paused') {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-900 ring-1 ring-amber-200/90">
          <span className="h-1.5 w-1.5 rounded-full bg-amber-400" aria-hidden />
          已暂停
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600 ring-1 ring-slate-200/90">
        <i className="fa-solid fa-check text-[9px] text-slate-400" aria-hidden />
        已完成
      </span>
    );
  }

  function renderCycleProgress(row: ForwardCycleTaskRow) {
    const planned = Math.max(1, row.planned_executions || 1);
    const needM = row.channel !== 'groups';
    const needG = row.channel !== 'moments';
    const mDone = Math.min(planned, row.moments_done_max ?? 0);
    const gDone = Math.min(planned, row.groups_done_max ?? 0);
    const robots = row.robots_progress || [];
    return (
      <div className="min-w-[11rem] space-y-2.5">
        {needM ? (
          <div>
            <div className="mb-1 flex items-center justify-between gap-2 text-[11px]">
              <span className="font-medium text-slate-600">发圈</span>
              <span className="font-mono tabular-nums text-slate-500">
                {mDone}/{planned}
              </span>
            </div>
            <ProgressBar done={mDone} total={planned} tone="teal" />
          </div>
        ) : null}
        {needG ? (
          <div>
            <div className="mb-1 flex items-center justify-between gap-2 text-[11px]">
              <span className="font-medium text-slate-600">群发</span>
              <span className="font-mono tabular-nums text-slate-500">
                {gDone}/{planned}
              </span>
            </div>
            <ProgressBar done={gDone} total={planned} tone="sky" />
          </div>
        ) : null}
        {robots.length > 0 ? (
          <div className="space-y-1 border-t border-slate-100 pt-2">
            {robots.slice(0, 3).map((rb) => (
              <div
                key={rb.id}
                className="truncate rounded-md bg-slate-50/90 px-1.5 py-0.5 text-[10px] leading-relaxed text-slate-500 ring-1 ring-slate-100"
                title={`${rb.name} · 发圈 ${rb.moments_done}/${planned} · 群发 ${rb.groups_done}/${planned}`}
              >
                <span className="font-medium text-slate-600">{rb.name}</span>
                <span className="ml-1 font-mono tabular-nums">
                  {needM ? `圈${rb.moments_done}` : ''}
                  {needM && needG ? ' · ' : ''}
                  {needG ? `群${rb.groups_done}` : ''}
                </span>
              </div>
            ))}
            {robots.length > 3 ? (
              <p className="text-[10px] text-slate-400">另有 {robots.length - 3} 台机器人…</p>
            ) : null}
          </div>
        ) : (
          <p className="text-[11px] text-slate-400">尚无机器人执行记录</p>
        )}
      </div>
    );
  }

  async function removeCompletedRobot(
    taskId: number,
    robotId: number,
    robotName: string,
    completionKind: 'groups' | 'moments',
  ) {
    const kindLabel = completionKind === 'moments' ? '发圈' : '群发';
    if (
      !window.confirm(
        `确定将「${robotName}」从「已完成${kindLabel}机器人」中移除？\n该机器人可再次拉取并执行${kindLabel}。`
      )
    ) {
      return;
    }
    const key = `${completionKind}:${taskId}:${robotId}`;
    setRemovingRobotKey(key);
    setMsg('');
    setNotice('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>(
        'remove_forward_task_completed_robot',
        {
          method: 'POST',
          body: JSON.stringify({
            site_id: siteId,
            task_id: taskId,
            robot_id: robotId,
            miniprogram_id: mpId,
            completion_kind: completionKind,
          }),
        }
      );
      if (!r.ok) {
        setMsg(r.detail || r.err || '移除失败');
        return;
      }
      setNotice(`已移除「${robotName}」的${kindLabel}完成记录`);
      await loadTasks();
    } finally {
      setRemovingRobotKey(null);
    }
  }

  function renderCompletedRobotChips(
    row: TaskRow,
    robots: CompletedRobotChip[] | undefined,
    completionKind: 'groups' | 'moments',
  ) {
    const list =
      robots ??
      (completionKind === 'groups'
        ? row.completed_groups_robots ?? row.completed_robots
        : row.completed_moments_robots) ??
      [];
    if (!list.length) {
      const fallbackIds =
        completionKind === 'groups'
          ? row.completed_groups_robot_ids ?? row.completed_robot_ids
          : row.completed_moments_robot_ids ?? [];
      if (fallbackIds.length) {
        return <span className="font-mono text-slate-600">{fallbackIds.join(', ')}</span>;
      }
      return <span className="text-slate-400">—</span>;
    }
    return (
      <ul className="m-0 flex max-w-[9.5rem] list-none flex-col gap-1.5 p-0">
        {list.map((rb) => {
          const rk = `${completionKind}:${row.id}:${rb.id}`;
          const busy = removingRobotKey === rk;
          return (
            <li
              key={`${completionKind}-${rb.id}`}
              className="flex w-full min-w-0 items-center gap-1 rounded-lg bg-slate-50 px-2 py-1 text-slate-800 ring-1 ring-slate-200/80"
              title={`点击 × 可移除该机器人的${completionKind === 'moments' ? '发圈' : '群发'}完成记录`}
            >
              <span className="min-w-0 flex-1 truncate text-xs leading-snug">{rb.name}</span>
              <button
                type="button"
                disabled={busy}
                aria-label={`移除 ${rb.name}`}
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-slate-400 transition hover:bg-rose-50 hover:text-rose-600 disabled:opacity-40"
                onClick={() => void removeCompletedRobot(row.id, rb.id, rb.name, completionKind)}
              >
                {busy ? <span className="text-[10px]">…</span> : <span className="text-sm leading-none">×</span>}
              </button>
            </li>
          );
        })}
      </ul>
    );
  }

  async function regenerateHashLink(taskId: number, entryId: number) {
    setMsg('');
    setNotice('');
    try {
      const r = await apiFetch<{ ok: boolean; hash_link?: string; err?: string; detail?: string }>(
        'regenerate_forward_task_hash_link',
        {
          method: 'POST',
          body: JSON.stringify({
            site_id: siteId,
            task_id: taskId,
            entry_id: entryId,
            miniprogram_id: mpId,
          }),
        }
      );
      if (r.ok && r.hash_link) {
        setNotice('已补生成小程序短链');
        await loadTasks();
      } else {
        setMsg(r.detail || r.err || '补生成失败，请检查小程序 AppID/Secret');
      }
    } catch (e) {
      setMsg(e instanceof Error ? e.message : '补生成失败');
    }
  }

  async function deleteTask(taskId: number) {
    if (!window.confirm('确定从转发队列中移除此条？机器人将不再拉取该队列项。')) return;
    setDeletingId(taskId);
    setMsg('');
    setNotice('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>('delete_forward_task', {
        method: 'POST',
        body: JSON.stringify({ site_id: siteId, task_id: taskId, miniprogram_id: mpId }),
      });
      if (!r.ok) {
        setMsg(r.detail || r.err || '删除失败');
        return;
      }
      await loadTasks();
    } finally {
      setDeletingId(null);
    }
  }

  if (mpId < 1 || siteId < 1) {
    return <p className="text-sm text-rose-700">无效的小程序或站点</p>;
  }

  const pages = Math.max(1, Math.ceil(total / perPage));
  const currentSiteModuleEnabled = robotModuleBySiteId[siteId] === true;
  const cycleActiveCount = cycleItems.filter((x) => x.status === 'active').length;
  const queueCountCurrent = queueAggregateById[siteId] ?? queueDirectById[siteId] ?? total;

  return (
    <div className="relative space-y-6">
      <div
        className="pointer-events-none absolute inset-x-0 -top-6 -z-10 h-64 bg-[radial-gradient(ellipse_at_top,_rgba(45,212,191,0.12),_transparent_55%)]"
        aria-hidden
      />

      <nav className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-slate-500">
        <Link to="/miniprograms" className="rounded-md px-1 py-0.5 transition hover:bg-slate-100 hover:text-teal-800">
          小程序管理
        </Link>
        <span className="text-slate-300" aria-hidden>
          /
        </span>
        <Link
          to={`/robot-miniprograms/${mpId}`}
          className="rounded-md px-1 py-0.5 transition hover:bg-slate-100 hover:text-teal-800"
        >
          {mpName || `小程序 #${mpId}`}
        </Link>
        <span className="text-slate-300" aria-hidden>
          /
        </span>
        <span className="font-medium text-slate-800">转发任务</span>
      </nav>

      <header className="overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-br from-white via-white to-teal-50/40 shadow-sm shadow-slate-900/[0.04]">
        <div className="flex flex-col gap-5 p-5 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900">转发任务</h1>
              {currentSiteModuleEnabled ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-800 ring-1 ring-emerald-200/80">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
                  转发已开启
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] font-semibold text-amber-900 ring-1 ring-amber-200/80">
                  转发未开启
                </span>
              )}
            </div>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600">
              {siteName ? (
                <>
                  当前站点{' '}
                  <span className="font-semibold text-slate-800">{siteName}</span>
                  <span className="ml-1 font-mono text-xs text-slate-400">#{siteId}</span>
                  ，小程序{' '}
                  <span className="font-semibold text-slate-800">{mpName || `#${mpId}`}</span>
                  {includesChildSites ? '（含下属二级站队列）' : ''}
                </>
              ) : (
                <span className="font-mono">站点 #{siteId}</span>
              )}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <div className="rounded-xl border border-teal-100/80 bg-white/80 px-3.5 py-2 shadow-sm">
                <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400">循环进行中</p>
                <p className="mt-0.5 text-lg font-semibold tabular-nums text-teal-800">{cycleActiveCount}</p>
              </div>
              <div className="rounded-xl border border-slate-200/80 bg-white/80 px-3.5 py-2 shadow-sm">
                <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400">队列任务</p>
                <p className="mt-0.5 text-lg font-semibold tabular-nums text-slate-800">{queueCountCurrent}</p>
              </div>
              <div className="rounded-xl border border-slate-200/80 bg-white/80 px-3.5 py-2 shadow-sm">
                <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400">循环合计</p>
                <p className="mt-0.5 text-lg font-semibold tabular-nums text-slate-800">{cycleTotal}</p>
              </div>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setCycleEdit(null);
                setCycleOpen(true);
              }}
              disabled={!currentSiteModuleEnabled}
            >
              <i className="fa-solid fa-arrows-rotate mr-1.5 text-[12px]" aria-hidden />
              添加循环任务
            </Button>
            <Button type="button" variant="primary" onClick={() => setAddOpen(true)} disabled={!currentSiteModuleEnabled}>
              <i className="fa-solid fa-plus mr-1.5 text-[12px]" aria-hidden />
              添加任务
            </Button>
            <Link to={`/robot-miniprograms/${mpId}`}>
              <Button type="button" variant="ghost">
                返回配置
              </Button>
            </Link>
          </div>
        </div>

        {siteDisplayRows.length > 0 ? (
          <div className="border-t border-slate-100/90 bg-slate-50/40 px-5 py-4 sm:px-6">
            <p className="mb-2.5 text-xs font-semibold tracking-wide text-slate-500">切换站点</p>
            <RobotSiteHierarchyChips
              rows={siteDisplayRows}
              activeSiteId={siteId}
              linkForSite={(id) => `/robot-miniprograms/${mpId}/sites/${id}/tasks`}
              queueDirectById={queueDirectById}
              queueAggregateById={queueAggregateById}
            />
          </div>
        ) : null}
      </header>

      {!currentSiteModuleEnabled ? (
        <div className="rounded-2xl border border-amber-200/90 bg-gradient-to-r from-amber-50 to-orange-50/50 px-4 py-3.5 text-sm leading-relaxed text-amber-950 shadow-sm">
          本站点在小程序「{mpName || `#${mpId}`}」下<strong className="font-semibold">转发模块未开启</strong>
          ，无法添加任务。请前往
          <Link to={`/robot-miniprograms/${mpId}`} className="mx-1 font-semibold text-teal-800 underline-offset-2 hover:underline">
            小程序机器人管理
          </Link>
          对「{siteName || `站点 #${siteId}`}」点击<strong className="font-semibold">开启转发</strong>。
        </div>
      ) : null}
      {msg ? <p className={inlineErrorClass}>{msg}</p> : null}
      {notice ? <p className={inlineSuccessClass}>{notice}</p> : null}

      <Card
        title="循环推广任务"
        titleExtra={
          <span className="rounded-full bg-white/80 px-2.5 py-0.5 text-[11px] font-medium text-slate-500 ring-1 ring-slate-200/80">
            固定短链 · 按频次循环
          </span>
        }
      >
        <p className="mb-4 text-sm leading-relaxed text-slate-600">
          按设定频次周期性推广固定小程序短链，与下方一次性入队队列相互独立。每个机器人独立计次，达到执行次数后自动停止。
        </p>

        <div className="mb-4 inline-flex flex-wrap gap-1 rounded-xl border border-slate-200/80 bg-slate-50/80 p-1">
          {(
            [
              { key: 'all', label: '全部' },
              { key: 'active', label: '进行中' },
              { key: 'paused', label: '已暂停' },
              { key: 'completed', label: '已完成' },
            ] as const
          ).map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setCycleStatusFilter(tab.key)}
              className={cn(
                'rounded-lg px-3 py-1.5 text-xs font-semibold transition',
                cycleStatusFilter === tab.key
                  ? 'bg-white text-teal-900 shadow-sm ring-1 ring-slate-200/90'
                  : 'text-slate-500 hover:text-slate-800',
              )}
            >
              {tab.label}
              {tab.key === 'all' && cycleTotal > 0 ? (
                <span className="ml-1 font-mono font-medium text-slate-400">{cycleTotal}</span>
              ) : null}
            </button>
          ))}
        </div>

        {cycleLoading ? (
          <Spinner label="加载循环任务" />
        ) : cycleItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-gradient-to-b from-slate-50/80 to-white px-6 py-12 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-50 text-teal-600 ring-1 ring-teal-100">
              <i className="fa-solid fa-arrows-rotate text-lg" aria-hidden />
            </div>
            <p className="mt-4 text-sm font-semibold text-slate-800">暂无循环任务</p>
            <p className="mt-1.5 max-w-sm text-xs leading-relaxed text-slate-500">
              添加后可按「每天 / 每 2 天 / 每 3 天 / 自定义」频次，持续推广同一小程序短链。
            </p>
            <Button
              type="button"
              variant="primary"
              className="mt-5"
              disabled={!currentSiteModuleEnabled}
              onClick={() => {
                setCycleEdit(null);
                setCycleOpen(true);
              }}
            >
              添加循环任务
            </Button>
          </div>
        ) : (
          <div className={tableWrapClass}>
            <table className={tableClass}>
              <thead className={theadClass}>
                <tr>
                  <th className={thClass}>任务</th>
                  <th className={cn(thClass, 'whitespace-nowrap')}>频次</th>
                  <th className={cn(thClass, 'min-w-[12rem]')}>进度</th>
                  <th className={cn(thClass, 'min-w-[11rem]')}>小程序短链</th>
                  <th className={thClass}>状态</th>
                  <th className={cn(thClass, 'text-right')}>操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100/90 bg-white">
                {cycleItems.map((row) => {
                  const hash = (row.hash_link || '').trim();
                  const planned = Math.max(1, row.planned_executions || 1);
                  return (
                    <tr key={row.id} className="align-top transition-colors hover:bg-teal-50/30">
                      <td className="px-4 py-4">
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-teal-50 to-sky-50 text-teal-700 ring-1 ring-teal-100/80">
                            <i className="fa-solid fa-link text-sm" aria-hidden />
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-slate-900">{row.title || `循环任务 #${row.id}`}</div>
                            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500">
                              <span className="font-mono text-slate-400">#{row.id}</span>
                              <span className="text-slate-300">·</span>
                              <span className="rounded-md bg-slate-50 px-1.5 py-0.5 ring-1 ring-slate-100">
                                {channelLabel(row.channel)}
                              </span>
                              <span className="text-slate-300">·</span>
                              <span>开始 {row.start_date || '—'}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="inline-flex flex-col gap-1.5">
                          <span className="inline-flex w-fit rounded-lg bg-teal-50 px-2.5 py-1 text-xs font-semibold text-teal-900 ring-1 ring-teal-100">
                            {row.freq_label}
                          </span>
                          <span className="text-[11px] text-slate-500">
                            各渠道计划 <span className="font-mono font-medium text-slate-700">{planned}</span> 次
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4">{renderCycleProgress(row)}</td>
                      <td className="max-w-xs px-4 py-4 sm:max-w-sm">
                        <HashLinkCell hash={hash} onCopy={() => void copyHashLink(hash)} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-4">{cycleStatusBadge(row.status)}</td>
                      <td className="px-4 py-4 text-right">
                        <div className="inline-flex flex-wrap justify-end gap-1.5">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={cycleBusyId === row.id}
                            onClick={() => {
                              setCycleEdit(row);
                              setCycleOpen(true);
                            }}
                          >
                            编辑
                          </Button>
                          {row.status === 'active' ? (
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              disabled={cycleBusyId === row.id}
                              onClick={() => void setCycleStatus(row, 'paused')}
                            >
                              暂停
                            </Button>
                          ) : null}
                          {row.status === 'paused' ? (
                            <Button
                              type="button"
                              variant="success"
                              size="sm"
                              disabled={cycleBusyId === row.id}
                              onClick={() => void setCycleStatus(row, 'active')}
                            >
                              恢复
                            </Button>
                          ) : null}
                          <Button
                            type="button"
                            variant="danger"
                            size="sm"
                            disabled={cycleBusyId === row.id}
                            onClick={() => void deleteCycleTask(row)}
                          >
                            删除
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card
        title="队列中的信息"
        titleExtra={
          <span className="rounded-full bg-white/80 px-2.5 py-0.5 text-[11px] font-medium text-slate-500 ring-1 ring-slate-200/80">
            一次性入队 · {total} 条
          </span>
        }
      >
        <p className="mb-4 text-sm leading-relaxed text-slate-600">
          仅显示归属当前小程序的转发队列；与其他小程序中同一站点的队列相互独立。
        </p>
        <div className="mb-4 flex flex-col gap-3 rounded-2xl border border-slate-200/70 bg-gradient-to-b from-slate-50/90 to-white p-3 sm:flex-row sm:flex-wrap sm:items-end sm:p-4">
          <label className="min-w-0 flex-1 text-sm text-slate-600">
            <span className="mb-1.5 block text-xs font-medium text-slate-500">筛选信息</span>
            <input
              className={cn(inputClass, 'font-mono')}
              placeholder="登记编号（精确）或标题 / 正文 / 编号关键词…"
              value={listQDraft}
              onChange={(e) => setListQDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') runListSearch();
              }}
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="primary" onClick={runListSearch} disabled={loading}>
              查询
            </Button>
            <Button type="button" variant="secondary" onClick={resetListSearch} disabled={!listQ && !listQDraft.trim()}>
              重置
            </Button>
          </div>
        </div>
        {listQ ? (
          <p className="mb-3 text-xs text-slate-500">
            当前筛选：<span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-slate-700">{listQ}</span>
            ，共 {total} 条
          </p>
        ) : null}
        {loading ? (
          <Spinner label="加载中" />
        ) : data.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-gradient-to-b from-slate-50/80 to-white px-6 py-12 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-500 ring-1 ring-slate-200/80">
              <i className="fa-solid fa-inbox text-lg" aria-hidden />
            </div>
            <p className="mt-4 text-sm font-semibold text-slate-800">
              {listQ.trim() ? '无匹配的转发任务' : '队列为空'}
            </p>
            <p className="mt-1.5 max-w-sm text-xs leading-relaxed text-slate-500">
              {listQ.trim()
                ? '可调整筛选条件或点击「重置」。'
                : '若策略为「管理员挑选转发」，请点击「添加任务」从信息列表加入。'}
            </p>
          </div>
        ) : (
          <div className={tableWrapClass}>
            <table className={tableClass}>
              <thead className={theadClass}>
                <tr>
                  <th className={thClass}>信息</th>
                  <th className={thClass}>类型</th>
                  {includesChildSites ? <th className={thClass}>所属站点</th> : null}
                  <th className={cn(thClass, 'min-w-[11rem]')}>小程序链接</th>
                  <th className={cn(thClass, 'w-[10rem] max-w-[10rem]')}>已完成发圈</th>
                  <th className={cn(thClass, 'w-[10rem] max-w-[10rem]')}>已完成群发</th>
                  <th className={thClass}>入队时间</th>
                  <th className={cn(thClass, 'text-right')}>操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100/90 bg-white">
                {data.map((row) => {
                  const hash = (row.miniprogram_hash_link ?? '').trim();
                  return (
                    <tr key={row.id} className="align-top transition-colors hover:bg-slate-50/80">
                      <td className="px-4 py-4">
                        <div className="font-mono text-[11px] font-medium text-slate-400">#{row.entry_id}</div>
                        <div className="mt-0.5 max-w-xs font-semibold text-slate-900 sm:max-w-md">
                          {(row.title_text || row.serial_number || '').trim() || '（无标题）'}
                        </div>
                        {row.body_text ? (
                          <div className="mt-1 max-w-xs truncate text-xs text-slate-500 sm:max-w-md">{row.body_text}</div>
                        ) : null}
                      </td>
                      <td className="whitespace-nowrap px-4 py-4">{entryTypeBadge(row.type_label, row.type_key)}</td>
                      {includesChildSites ? (
                        <td className="whitespace-nowrap px-4 py-4 text-xs text-slate-700">
                          {(() => {
                            const sid = row.site_id ?? siteId;
                            const nm = siteNameById.get(sid);
                            return nm ? (
                              <span>
                                {nm}
                                <span className="ml-1 font-mono text-slate-400">#{sid}</span>
                              </span>
                            ) : (
                              <span className="font-mono">#{sid}</span>
                            );
                          })()}
                        </td>
                      ) : null}
                      <td className="max-w-xs px-4 py-4 sm:max-w-sm">
                        <HashLinkCell
                          hash={hash}
                          onCopy={() => void copyHashLink(hash)}
                          onRegen={() => void regenerateHashLink(row.id, row.entry_id)}
                        />
                      </td>
                      <td className="w-[10rem] max-w-[10rem] px-4 py-4 text-xs text-slate-700">
                        {renderCompletedRobotChips(row, row.completed_moments_robots, 'moments')}
                      </td>
                      <td className="w-[10rem] max-w-[10rem] px-4 py-4 text-xs text-slate-700">
                        {renderCompletedRobotChips(row, row.completed_groups_robots ?? row.completed_robots, 'groups')}
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 text-xs text-slate-500">{row.created_at}</td>
                      <td className="px-4 py-4 text-right">
                        <Button
                          type="button"
                          variant="danger"
                          size="sm"
                          disabled={deletingId === row.id}
                          onClick={() => void deleteTask(row.id)}
                        >
                          {deletingId === row.id ? '删除中…' : '移出队列'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {total > 0 ? (
          <PaginationBar
            className="mt-4"
            page={page}
            totalPages={pages}
            total={total}
            summary={`共 ${total} 条，每页 ${perPage} 条`}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => Math.min(pages, p + 1))}
          />
        ) : null}
      </Card>

      {addOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-[2px]" role="dialog">
          <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-2xl shadow-slate-900/20">
            <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50/90 to-white px-5 py-4">
              <h3 className="text-lg font-semibold tracking-tight text-slate-900">选择信息加入转发队列</h3>
              <div className="mt-3 flex gap-2">
                <input
                  className={cn(inputClass, 'flex-1')}
                  placeholder="搜索标题、正文、编号…"
                  value={pickerQ}
                  onChange={(e) => {
                    setPickerQ(e.target.value);
                    setPickerPage(1);
                  }}
                />
                <Button type="button" variant="secondary" onClick={() => void loadPicker()}>
                  搜索
                </Button>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-3">
              {pickerLoading ? (
                <Spinner label="加载信息" />
              ) : picker.length === 0 ? (
                <p className="py-8 text-center text-sm text-slate-500">没有可展示的信息。</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {picker.map((e) => (
                    <li key={e.id} className="flex flex-wrap items-center justify-between gap-2 py-3.5">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="font-mono text-xs text-slate-400">#{e.id}</div>
                          {entryTypeBadge(e.type_label, e.type_key)}
                        </div>
                        <div className="mt-0.5 truncate font-medium text-slate-800">
                          {(e.title_text || e.serial_number || '').trim() || '（无标题）'}
                        </div>
                      </div>
                      <Button type="button" variant="primary" size="sm" className="shrink-0" onClick={() => void addTask(e.id)}>
                        加入转发
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 bg-slate-50/40 px-5 py-3">
              {pickerTotal > 0 ? (
                <PaginationBar
                  page={pickerPage}
                  totalPages={Math.max(1, Math.ceil(pickerTotal / 12))}
                  total={pickerTotal}
                  onPrev={() => setPickerPage((p) => Math.max(1, p - 1))}
                  onNext={() => setPickerPage((p) => Math.min(Math.max(1, Math.ceil(pickerTotal / 12)), p + 1))}
                />
              ) : (
                <span />
              )}
              <Button type="button" variant="secondary" onClick={() => setAddOpen(false)}>
                关闭
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <ForwardCycleTaskModal
        open={cycleOpen}
        mpId={mpId}
        siteId={siteId}
        editItem={cycleEdit}
        onClose={() => {
          setCycleOpen(false);
          setCycleEdit(null);
        }}
        onSaved={() => {
          setNotice(cycleEdit ? '循环任务已更新' : '循环任务已创建');
          void loadCycleTasks();
        }}
      />
    </div>
  );
}
